import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

export type ProductStatus = "active" | "deprecated" | "retired";
export type ProductModel = "query" | "seat" | "device";

export interface ProductRecord {
    id: string;
    slug: string;
    name: string;
    model: ProductModel;
    vendor: string;
    description: string;
    status: ProductStatus;
    icon?: string;
    capabilities: string[];
    licenseCount: number;
    createdAt: string;
    updatedAt: string;
}

export interface CapabilityRecord {
    slug: string;
    name: string;
    description: string;
    sortOrder: number;
}

const PRODUCTS_KEY = ["admin", "products"] as const;
const PRODUCT_KEY = (slug: string) => ["admin", "products", slug] as const;
const CAPABILITIES_KEY = ["admin", "capabilities"] as const;

export function useProducts() {
    return useQuery({
        queryKey: PRODUCTS_KEY,
        queryFn: () =>
            api.get<{ products: ProductRecord[] }>("/api/v2/products").then((d) => d.products),
        staleTime: 30_000,
    });
}

export function useProduct(slug: string | undefined) {
    return useQuery({
        queryKey: slug ? PRODUCT_KEY(slug) : ["admin", "products", "none"],
        queryFn: () =>
            api.get<{ product: ProductRecord }>(`/api/v2/products/${slug}`).then((d) => d.product),
        enabled: !!slug,
    });
}

export function useCapabilities() {
    return useQuery({
        queryKey: CAPABILITIES_KEY,
        queryFn: () =>
            api.get<{ capabilities: CapabilityRecord[] }>("/api/v2/capabilities").then((d) => d.capabilities),
        staleTime: 5 * 60_000,
    });
}

export interface CreateProductInput {
    slug: string;
    name: string;
    model: ProductModel;
    vendor?: string;
    description?: string;
    icon?: string;
    capabilities?: string[];
}

export function useCreateProduct() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateProductInput) =>
            api.post<{ product: ProductRecord }>("/api/v2/products", input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: PRODUCTS_KEY });
        },
    });
}

export interface UpdateProductInput {
    slug: string; // path key, immutable
    name?: string;
    vendor?: string;
    description?: string;
    status?: ProductStatus;
    icon?: string;
    clearIcon?: boolean;
}

export function useUpdateProduct() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ slug, ...patch }: UpdateProductInput) =>
            api.patch<{ product: ProductRecord }>(`/api/v2/products/${slug}`, patch),
        onSuccess: (_data, variables) => {
            void qc.invalidateQueries({ queryKey: PRODUCTS_KEY });
            void qc.invalidateQueries({ queryKey: PRODUCT_KEY(variables.slug) });
        },
    });
}

export function useDeleteProduct() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (slug: string) => api.delete<void>(`/api/v2/products/${slug}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: PRODUCTS_KEY });
        },
    });
}

export function useSetProductCapabilities() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ slug, capabilities }: { slug: string; capabilities: string[] }) =>
            api.put<{ product: ProductRecord }>(`/api/v2/products/${slug}/capabilities`, {
                capabilities,
            }),
        onSuccess: (_data, variables) => {
            void qc.invalidateQueries({ queryKey: PRODUCTS_KEY });
            void qc.invalidateQueries({ queryKey: PRODUCT_KEY(variables.slug) });
        },
    });
}

/**
 * Mapping from IoC type slug to the product capabilities that cover it.
 * Mirrors internal/license/capabilities.go; if the product has at least one
 * capability in the mapped set, the IoC type is considered supported.
 *
 * Keep in sync with iocCapabilityMap in the Go side.
 */
export const IOC_CAPABILITY_MAP: Readonly<Record<string, readonly string[]>> = {
    ip: ["network"],
    cidr: ["network"],
    domain: ["network"],
    url: ["network"],
    email: ["email"],
    certificate: ["certificate", "network"],
    hash: ["file", "executable"],
};

/**
 * Returns true when a product with the given capability set supports
 * grants for the given IoC type. An IoC type with no mapping (unknown) is
 * permitted — we don't tighten beyond what the server enforces.
 */
export function productSupportsIoC(
    productCapabilities: readonly string[],
    iocType: string,
): boolean {
    const need = IOC_CAPABILITY_MAP[iocType];
    if (!need) return true;
    return need.some((cap) => productCapabilities.includes(cap));
}
